import type { CSSProperties } from 'react';

interface CategoryBadgeProps {
  name: string;
  color?: string;
  small?: boolean;
  onRemove?: () => void;
}

/**
 * Category pill. Colors are user-defined per category (hex from
 * intelligence_chat_categories.color), so they stay as inline styles rather
 * than Tailwind hue classes — but the pill shape follows the spec recipe and
 * dark mode gets a lightened text color + stronger tint via CSS variables so
 * the badge stays legible on gray-800 surfaces.
 */
export function CategoryBadge({ name, color = '#6B7280', small = false, onRemove }: CategoryBadgeProps) {
  // Convert hex color to RGB for background with opacity
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 107, g: 114, b: 128 }; // Default gray
  };

  const rgb = hexToRgb(color);
  // Lightened variant keeps user-defined hues readable on dark surfaces.
  const light = {
    r: Math.min(255, Math.round(rgb.r + (255 - rgb.r) * 0.55)),
    g: Math.min(255, Math.round(rgb.g + (255 - rgb.g) * 0.55)),
    b: Math.min(255, Math.round(rgb.b + (255 - rgb.b) * 0.55)),
  };
  const vars = {
    '--cat-bg': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
    '--cat-bg-dark': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.28)`,
    '--cat-text': color,
    '--cat-text-dark': `rgb(${light.r}, ${light.g}, ${light.b})`,
  } as CSSProperties;

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full font-medium bg-[var(--cat-bg)] text-[var(--cat-text)] dark:bg-[var(--cat-bg-dark)] dark:text-[var(--cat-text-dark)] ${
        small ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm'
      }`}
      style={vars}
    >
      <span>{name}</span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-1 transition-opacity duration-150 hover:opacity-70"
        >
          ×
        </button>
      )}
    </span>
  );
}
